import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuth } from 'firebase/auth';
import { makeAuthenticatedApiCall } from '../apiHelper';
import {
  LeadsVoAdminAgentDetail,
  LeadsVoAgent,
  LeadsVoDashboardStats,
  LeadsVoPack,
  LeadsVoRdv,
  LeadsVoRdvListResponse,
} from '../types/leadsVo';

const uid = () => getAuth().currentUser?.uid ?? 'anon';

// ─── Partner config hooks ───

export const useResearchOptions = () => {
  return useQuery<{ brands: string[]; carburants: string[]; boites: string[]; types_carrosserie: string[] }>({
    queryKey: ['leadsVoResearchOptions'],
    queryFn: async () => {
      const res = await makeAuthenticatedApiCall('get', '/api/leads-vo/research-options');
      return res.data as { brands: string[]; carburants: string[]; boites: string[]; types_carrosserie: string[] };
    },
    staleTime: 24 * 60 * 60 * 1000,
  });
};

export const useSearchCities = (query: string) => {
  return useQuery<{ cities: string[] }>({
    queryKey: ['leadsVoCities', query],
    queryFn: async () => {
      const res = await makeAuthenticatedApiCall('get', `/api/leads-vo/cities?q=${encodeURIComponent(query)}`);
      return res.data as { cities: string[] };
    },
    enabled: query.length >= 2,
  });
};

export const useServiceAccountEmail = () => {
  return useQuery<{ email: string }>({
    queryKey: ['leadsVoServiceAccountEmail'],
    queryFn: async () => {
      const res = await makeAuthenticatedApiCall('get', '/api/leads-vo/service-account-email');
      return res.data;
    },
    staleTime: 24 * 60 * 60 * 1000,
  });
};

// ─── Agency hooks ───

export const useLeadsVoAgent = () => {
  return useQuery<LeadsVoAgent>({
    queryKey: ['leadsVoAgent', uid()],
    queryFn: async () => {
      const res = await makeAuthenticatedApiCall('get', '/api/leads-vo/agent');
      return res.data as LeadsVoAgent;
    },
    retry: false,
  });
};

export const useLeadsVoResearch = () => {
  return useQuery<{ research: any }>({
    queryKey: ['leadsVoResearch', uid()],
    queryFn: async () => {
      const res = await makeAuthenticatedApiCall('get', '/api/leads-vo/agent/research');
      return res.data as { research: any };
    },
    retry: false,
  });
};

export const useLeadsVoPacks = () => {
  return useQuery<LeadsVoPack[]>({
    queryKey: ['leadsVoPacks', uid()],
    queryFn: async () => {
      const res = await makeAuthenticatedApiCall('get', '/api/leads-vo/packs');
      return res.data;
    },
    // Tant qu'un pack est en attente de paiement (ex: CB Stripe dont le webhook
    // n'est pas encore arrivé), on rafraîchit pour basculer en "actif" automatiquement.
    refetchInterval: (query) => {
      const packs = query.state.data;
      const hasPending = Array.isArray(packs) && packs.some((p) => p.status === 'pending_payment');
      return hasPending ? 4000 : false;
    },
  });
};

export const useLeadsVoRdvs = (page: number, limit: number, statusFilter?: string) => {
  return useQuery<LeadsVoRdvListResponse>({
    queryKey: ['leadsVoRdvs', uid(), page, limit, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (statusFilter) params.append('status', statusFilter);
      const res = await makeAuthenticatedApiCall('get', `/api/leads-vo/rdvs?${params}`);
      return res.data as LeadsVoRdvListResponse;
    },
  });
};

interface OrderPackArgs {
  size: number;
  payment_method: 'qonto_transfer' | 'stripe_card';
}

export const useOrderPack = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ size, payment_method }: OrderPackArgs) => {
      const res = await makeAuthenticatedApiCall('post', '/api/leads-vo/packs', { size, payment_method });
      return res.data as {
        pack: any;
        payment: {
          method: 'qonto_transfer' | 'stripe_card';
          invoice_number?: string;
          bank_transfer?: { iban: string; bic: string; account_holder: string; reference: string };
          client_secret?: string;
          publishable_key?: string;
        };
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadsVoPacks'] });
    },
  });
};

// Confirme une session Stripe Embedded au retour (?stripe_session=...) : règle le pack.
export const useConfirmStripeSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const res = await makeAuthenticatedApiCall(
        'get',
        `/api/leads-vo/packs/confirm-stripe?session_id=${encodeURIComponent(sessionId)}`,
      );
      return res.data as { status: string; paid: boolean };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadsVoPacks'] });
    },
  });
};

// Demande d'activation Leads VO depuis la home pour une agence non encore activée.
// Le backend envoie un email à l'admin et retourne un status :
//   'sent'              -> 1er clic, email parti
//   'already_requested' -> l'agence a déjà un record leads_vo_agents (admin doit finir l'activation)
//   'already_active'    -> compte déjà actif (impossible normalement vu où le bouton est affiché)
export const useRequestLeadsVoActivation = () => {
  return useMutation({
    mutationFn: async () => {
      const res = await makeAuthenticatedApiCall('post', '/api/leads-vo/request-activation');
      return res.data as { status: 'sent' | 'already_requested' | 'already_active' };
    },
  });
};

// Reprend le paiement CB d'un pack laissé en attente : renvoie un nouveau client_secret.
export const useResumePackPayment = () => {
  return useMutation({
    mutationFn: async (packId: number) => {
      const res = await makeAuthenticatedApiCall('post', `/api/leads-vo/packs/${packId}/resume-payment`);
      return res.data as { client_secret: string; publishable_key: string };
    },
  });
};

// Annulation par l'agence d'un de ses packs en attente de paiement.
export const useCancelOwnPack = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (packId: number) => {
      const res = await makeAuthenticatedApiCall('post', `/api/leads-vo/packs/${packId}/cancel`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadsVoPacks'] });
    },
  });
};

// Téléchargement de la facture Qonto d'un pack (PDF relayé par le backend).
export const useDownloadPackInvoice = () =>
  useMutation({
    mutationFn: async (pack: LeadsVoPack) => {
      const res = await makeAuthenticatedApiCall('get', `/api/leads-vo/packs/${pack.id}/invoice`, undefined, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data as BlobPart], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture-${pack.qonto_invoice_number ?? `pack-${pack.id}`}.pdf`;
      link.click();
      // Révocation différée : une révocation synchrone peut annuler le téléchargement (Safari, Firefox).
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    },
  });

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (profile: Record<string, any>) => {
      const res = await makeAuthenticatedApiCall('put', '/api/leads-vo/agent/profile', profile);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadsVoAgent'] });
    },
  });
};

export const useCancelRdv = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ rdvId, motif }: { rdvId: string; motif: string }) => {
      const res = await makeAuthenticatedApiCall('post', `/api/leads-vo/rdvs/${rdvId}/cancel`, { motif });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadsVoRdvs'] });
      queryClient.invalidateQueries({ queryKey: ['leadsVoPacks'] });
    },
  });
};

export const useUpdateZone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (zone: Record<string, any>) => {
      const res = await makeAuthenticatedApiCall('put', '/api/leads-vo/agent/zone', zone);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadsVoAgent'] });
    },
  });
};

export const useUpdateResearch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (research: Record<string, any>) => {
      const res = await makeAuthenticatedApiCall('put', '/api/leads-vo/agent/research', research);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadsVoAgent'] });
    },
  });
};

export const useUpdateBusinessHours = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (businessHours: Record<string, any>) => {
      const res = await makeAuthenticatedApiCall('put', '/api/leads-vo/agent/business-hours', { business_hours: businessHours });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadsVoAgent'] });
    },
  });
};

export const useUpdateCalendar = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (calendarId: string) => {
      const res = await makeAuthenticatedApiCall('put', '/api/leads-vo/agent/calendar', { calendar_id: calendarId });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadsVoAgent'] });
    },
  });
};

export const useReportNoShow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ rdvId, motif }: { rdvId: number; motif: string }) => {
      const res = await makeAuthenticatedApiCall('post', `/api/leads-vo/rdvs/${rdvId}/no-show`, { motif });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadsVoRdvs'] });
      queryClient.invalidateQueries({ queryKey: ['leadsVoPacks'] });
    },
  });
};

// ─── Admin hooks ───

export const useAdminLeadsVoAgents = () => {
  return useQuery<LeadsVoAgent[]>({
    queryKey: ['adminLeadsVoAgents'],
    queryFn: async () => {
      const res = await makeAuthenticatedApiCall('get', '/api/leads-vo/admin/agents');
      return res.data;
    },
  });
};

export const useAdminLeadsVoPacks = () => {
  return useQuery<LeadsVoPack[]>({
    queryKey: ['adminLeadsVoPacks'],
    queryFn: async () => {
      const res = await makeAuthenticatedApiCall('get', '/api/leads-vo/admin/packs');
      return res.data;
    },
  });
};

export const useConfirmPackPayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (packId: number) => {
      const res = await makeAuthenticatedApiCall('post', `/api/leads-vo/admin/packs/${packId}/confirm-payment`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminLeadsVoPacks'] });
    },
  });
};

export const useCancelPack = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (packId: number) => {
      const res = await makeAuthenticatedApiCall('post', `/api/leads-vo/admin/packs/${packId}/cancel`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminLeadsVoPacks'] });
    },
  });
};

export const useAdminLeadsVoRdvs = (page: number, limit: number, statusFilter?: string, agentId?: number) => {
  return useQuery<LeadsVoRdvListResponse>({
    queryKey: ['adminLeadsVoRdvs', page, limit, statusFilter, agentId],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (statusFilter) params.append('status', statusFilter);
      if (agentId) params.append('agent_id', String(agentId));
      const res = await makeAuthenticatedApiCall('get', `/api/leads-vo/admin/rdvs?${params}`);
      return res.data as LeadsVoRdvListResponse;
    },
  });
};

export const useAdminLeadsVoAgentDetail = (agentId: number) => {
  return useQuery<LeadsVoAdminAgentDetail>({
    queryKey: ['adminLeadsVoAgentDetail', agentId],
    queryFn: async () => {
      const res = await makeAuthenticatedApiCall('get', `/api/leads-vo/admin/agents/${agentId}`);
      return res.data as LeadsVoAdminAgentDetail;
    },
    enabled: !!agentId,
  });
};

// Édition des paramètres d'un agent par un admin (mêmes payloads que les hooks agence).
export const useAdminUpdateAgentZone = (agentId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (zone: Record<string, any>) => {
      const res = await makeAuthenticatedApiCall('put', `/api/leads-vo/admin/agents/${agentId}/zone`, zone);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminLeadsVoAgentDetail', agentId] });
    },
  });
};

// Admin : génère un pack pour l'agence et lui envoie le lien/instructions de paiement par email.
export const useAdminSendPackPaymentLink = (agentId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ size, payment_method }: { size: number; payment_method: 'stripe_card' | 'qonto_transfer' }) => {
      const res = await makeAuthenticatedApiCall('post', `/api/leads-vo/admin/agents/${agentId}/payment-link`, {
        size,
        payment_method,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminLeadsVoAgentDetail', agentId] });
      queryClient.invalidateQueries({ queryKey: ['adminLeadsVoPacks'] });
    },
  });
};

// Admin : offre des RDV à l'agence (pack sans paiement, provisionné immédiatement).
export const useAdminGiftPack = (agentId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ size, reason }: { size: number; reason: string }) => {
      const res = await makeAuthenticatedApiCall('post', `/api/leads-vo/admin/agents/${agentId}/gift-pack`, {
        size,
        reason,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminLeadsVoAgentDetail', agentId] });
      queryClient.invalidateQueries({ queryKey: ['adminLeadsVoPacks'] });
    },
  });
};

export const useAdminUpdateAgentResearch = (agentId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (research: Record<string, any>) => {
      const res = await makeAuthenticatedApiCall('put', `/api/leads-vo/admin/agents/${agentId}/research`, research);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminLeadsVoAgentDetail', agentId] });
    },
  });
};

export const useAdminUpdateAgentBusinessHours = (agentId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (businessHours: Record<string, any>) => {
      const res = await makeAuthenticatedApiCall('put', `/api/leads-vo/admin/agents/${agentId}/business-hours`, {
        business_hours: businessHours,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminLeadsVoAgentDetail', agentId] });
    },
  });
};

export const useAdminCreateAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (organizationId: number) => {
      const res = await makeAuthenticatedApiCall('post', '/api/leads-vo/admin/agents', { organization_id: organizationId });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminLeadsVoAgents'] });
      queryClient.invalidateQueries({ queryKey: ['adminLeadsVoDashboard'] });
    },
  });
};

export const useAdminToggleEnabled = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (agentId: number) => {
      const res = await makeAuthenticatedApiCall('patch', `/api/leads-vo/admin/agents/${agentId}/toggle-enabled`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminLeadsVoAgents'] });
    },
  });
};

export const useAdminLeadsVoDashboard = () => {
  return useQuery<LeadsVoDashboardStats>({
    queryKey: ['adminLeadsVoDashboard'],
    queryFn: async () => {
      const res = await makeAuthenticatedApiCall('get', '/api/leads-vo/admin/dashboard');
      return res.data;
    },
  });
};
